<script>
  import { onMount, onDestroy } from "svelte";
  import { BASE_URL } from "../services/service.config";
  import { terminalStream } from "../services/TerminalStreamService";

  export let symbol = "";
  export let scripCode = "";
  export let ucc = "";
  export let isOpen = false;
  export let onClose = () => {};

  let canvas;
  let ctx;
  let chartData = null;
  let isLoading = true;
  let errorMessage = null;
  let streamUnsub = null;

  let crosshairX = null;
  let crosshairY = null;
  let hoveredCandle = null;

  async function fetchChartData() {
    if (!symbol && !scripCode) return;
    isLoading = true;
    errorMessage = null;
    try {
      const targetParam = symbol || scripCode;
      const url = ucc
        ? `${BASE_URL}/api/v1/engine/chart/client/${encodeURIComponent(ucc)}/${encodeURIComponent(targetParam)}`
        : `${BASE_URL}/api/v1/engine/chart/scrip/${encodeURIComponent(targetParam)}`;
      
      const res = await fetch(url);
      if (!res.ok) {
        throw new Error(`Server returned HTTP ${res.status}`);
      }
      chartData = await res.json();
      requestAnimationFrame(drawChart);
    } catch (err) {
      console.error("Failed to load chart data:", err);
      errorMessage = err.message || "Failed to load signal chart";
    } finally {
      isLoading = false;
    }
  }

  function handleMouseMove(e) {
    if (!canvas || !chartData || !chartData.candles || chartData.candles.length === 0) return;
    const rect = canvas.getBoundingClientRect();
    crosshairX = e.clientX - rect.left;
    crosshairY = e.clientY - rect.top;
    drawChart();
  }

  function handleMouseLeave() {
    crosshairX = null;
    crosshairY = null;
    hoveredCandle = null;
    drawChart();
  }

  function drawChart() {
    if (!canvas) return;
    ctx = canvas.getContext("2d");
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;

    // Background
    ctx.fillStyle = "#0f172a";
    ctx.fillRect(0, 0, width, height);

    if (!chartData || !chartData.candles || chartData.candles.length === 0) {
      ctx.fillStyle = "#64748b";
      ctx.font = "14px Inter, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("No candle data available for this scrip", width / 2, height / 2);
      return;
    }

    const candles = chartData.candles;
    const margin = { top: 40, right: 70, bottom: 30, left: 20 };
    const chartW = width - margin.left - margin.right;
    const chartH = height - margin.top - margin.bottom;

    // Calculate Price Bounds
    let minPrice = Infinity;
    let maxPrice = -Infinity;

    for (const c of candles) {
      if (c.low < minPrice) minPrice = c.low;
      if (c.high > maxPrice) maxPrice = c.high;
      if (c.vwap && c.vwap > 0) {
        if (c.vwap < minPrice) minPrice = c.vwap;
        if (c.vwap > maxPrice) maxPrice = c.vwap;
      }
    }

    // Include Trailing SL, Target, and Down-Avg in Bounds if active
    const active = chartData.active_position;
    if (active) {
      if (active.target_price > 0 && active.target_price > maxPrice) maxPrice = active.target_price;
      if (active.trailing_sl_price > 0 && active.trailing_sl_price < minPrice) minPrice = active.trailing_sl_price;
      if (active.down_avg_levels) {
        for (const lvl of active.down_avg_levels) {
          if (lvl < minPrice) minPrice = lvl;
        }
      }
    }

    // Add 1.5% padding on top & bottom
    const pricePadding = (maxPrice - minPrice) * 0.05 || 2.0;
    minPrice -= pricePadding;
    maxPrice += pricePadding;
    const priceRange = maxPrice - minPrice;

    const getY = (price) => margin.top + chartH - ((price - minPrice) / priceRange) * chartH;
    const barW = Math.max(2, Math.floor(chartW / candles.length) - 2);

    // Grid lines (horizontal)
    const gridSteps = 5;
    ctx.lineWidth = 1;
    ctx.strokeStyle = "#1e293b";
    ctx.fillStyle = "#64748b";
    ctx.font = "11px tabular-nums, monospace";
    ctx.textAlign = "left";

    for (let i = 0; i <= gridSteps; i++) {
      const p = minPrice + (priceRange / gridSteps) * i;
      const y = getY(p);
      ctx.beginPath();
      ctx.moveTo(margin.left, y);
      ctx.lineTo(width - margin.right, y);
      ctx.stroke();

      ctx.fillText(`₹${p.toFixed(2)}`, width - margin.right + 6, y + 4);
    }

    // Draw Candlesticks
    hoveredCandle = null;
    for (let i = 0; i < candles.length; i++) {
      const c = candles[i];
      const x = margin.left + i * (chartW / candles.length) + (chartW / candles.length - barW) / 2;
      const openY = getY(c.open);
      const closeY = getY(c.close);
      const highY = getY(c.high);
      const lowY = getY(c.low);

      const isBullish = c.close >= c.open;
      const candleColor = isBullish ? "#16a34a" : "#dc2626";

      // Wick
      ctx.strokeStyle = candleColor;
      ctx.beginPath();
      ctx.moveTo(x + barW / 2, highY);
      ctx.lineTo(x + barW / 2, lowY);
      ctx.stroke();

      // Body
      ctx.fillStyle = candleColor;
      const bodyTop = Math.min(openY, closeY);
      const bodyH = Math.max(2, Math.abs(closeY - openY));
      ctx.fillRect(x, bodyTop, barW, bodyH);

      // Check Hover
      if (crosshairX !== null && crosshairX >= x && crosshairX <= x + (chartW / candles.length)) {
        hoveredCandle = c;
      }
    }

    // Draw Intraday VWAP Line
    ctx.strokeStyle = "#3b82f6";
    ctx.lineWidth = 2;
    ctx.beginPath();
    let started = false;
    for (let i = 0; i < candles.length; i++) {
      const c = candles[i];
      if (!c.vwap || c.vwap <= 0) continue;
      const x = margin.left + i * (chartW / candles.length) + (chartW / candles.length) / 2;
      const y = getY(c.vwap);
      if (!started) {
        ctx.moveTo(x, y);
        started = true;
      } else {
        ctx.lineTo(x, y);
      }
    }
    ctx.stroke();

    // Overlays for Active Position: Target, Trailing SL, Down-Avg
    if (active) {
      // 1. Profit Target Line (Dashed Emerald)
      if (active.target_price > 0) {
        const y = getY(active.target_price);
        ctx.setLineDash([6, 4]);
        ctx.strokeStyle = "#10b981";
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(margin.left, y);
        ctx.lineTo(width - margin.right, y);
        ctx.stroke();
        ctx.fillStyle = "#10b981";
        ctx.fillText(`Target: ₹${active.target_price.toFixed(2)}`, margin.left + 8, y - 6);
      }

      // 2. Dynamic Trailing Stop Loss Line (Dashed Crimson)
      if (active.trailing_sl_price > 0) {
        const y = getY(active.trailing_sl_price);
        ctx.setLineDash([6, 4]);
        ctx.strokeStyle = "#ef4444";
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(margin.left, y);
        ctx.lineTo(width - margin.right, y);
        ctx.stroke();
        ctx.fillStyle = "#ef4444";
        ctx.fillText(`Trailing SL: ₹${active.trailing_sl_price.toFixed(2)}`, margin.left + 8, y - 6);
      }

      // 3. Down-Averaging Dip Levels (Dotted Amber)
      if (active.down_avg_levels && active.down_avg_levels.length > 0) {
        ctx.setLineDash([2, 4]);
        ctx.strokeStyle = "#f59e0b";
        ctx.lineWidth = 1;
        for (let idx = 0; idx < active.down_avg_levels.length; idx++) {
          const lvl = active.down_avg_levels[idx];
          const y = getY(lvl);
          ctx.beginPath();
          ctx.moveTo(margin.left, y);
          ctx.lineTo(width - margin.right, y);
          ctx.stroke();
          ctx.fillStyle = "#f59e0b";
          ctx.fillText(`Down-Avg ${idx + 1}: ₹${lvl.toFixed(2)}`, width - margin.right - 130, y - 4);
        }
      }

      ctx.setLineDash([]); // Reset line dash
    }

    // Draw Execution Markers (Triangles)
    if (chartData.executions && chartData.executions.length > 0) {
      for (const exec of chartData.executions) {
        // Approximate time matching or anchor to recent bar
        const execY = getY(exec.price);
        let execX = margin.left + (chartW / 2);
        // Find closest candle by time
        let closestIdx = -1;
        let minDiff = Infinity;
        for (let i = 0; i < candles.length; i++) {
          const diff = Math.abs(candles[i].time - exec.time);
          if (diff < minDiff) {
            minDiff = diff;
            closestIdx = i;
          }
        }
        if (closestIdx !== -1) {
          execX = margin.left + closestIdx * (chartW / candles.length) + (chartW / candles.length) / 2;
        }

        ctx.fillStyle = exec.side === "BUY" ? "#22c55e" : "#f43f5e";
        ctx.beginPath();
        if (exec.side === "BUY") {
          ctx.moveTo(execX, execY + 14);
          ctx.lineTo(execX - 6, execY + 24);
          ctx.lineTo(execX + 6, execY + 24);
        } else {
          ctx.moveTo(execX, execY - 14);
          ctx.lineTo(execX - 6, execY - 24);
          ctx.lineTo(execX + 6, execY - 24);
        }
        ctx.closePath();
        ctx.fill();
      }
    }

    // Crosshair rendering
    if (crosshairX !== null && crosshairY !== null) {
      ctx.setLineDash([3, 3]);
      ctx.strokeStyle = "#475569";
      ctx.lineWidth = 1;

      // Vertical line
      ctx.beginPath();
      ctx.moveTo(crosshairX, margin.top);
      ctx.lineTo(crosshairX, height - margin.bottom);
      ctx.stroke();

      // Horizontal line
      ctx.beginPath();
      ctx.moveTo(margin.left, crosshairY);
      ctx.lineTo(width - margin.right, crosshairY);
      ctx.stroke();
      ctx.setLineDash([]);
    }
  }

  function handleTickDelta(delta) {
    if (!chartData || !chartData.candles || chartData.candles.length === 0) return;
    if (delta.symbol === symbol || delta.scrip_code === scripCode) {
      const ltp = Number(delta.ltp);
      if (ltp > 0) {
        const last = chartData.candles[chartData.candles.length - 1];
        if (ltp > last.high) last.high = ltp;
        if (ltp < last.low) last.low = ltp;
        last.close = ltp;
        if (chartData.active_position) {
          chartData.active_position.current_ltp = ltp;
        }
        requestAnimationFrame(drawChart);
      }
    }
  }

  $: if (isOpen && (symbol || scripCode)) {
    fetchChartData();
  }

  onMount(() => {
    streamUnsub = terminalStream.on("delta", handleTickDelta);
  });

  onDestroy(() => {
    if (streamUnsub) streamUnsub();
  });
</script>

{#if isOpen}
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <div class="signal-chart-backdrop" on:click={onClose}>
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <div class="signal-chart-modal" on:click|stopPropagation>
      <!-- Header Strip -->
      <div class="chart-header">
        <div class="chart-title-area">
          <span class="chart-symbol">{symbol || scripCode}</span>
          {#if scripCode && symbol && scripCode !== symbol}
            <span class="chart-scrip-code">({scripCode})</span>
          {/if}
          {#if ucc}
            <span class="chart-badge-ucc">UCC: {ucc}</span>
          {/if}
        </div>

        <div class="chart-hud-metrics">
          {#if hoveredCandle}
            <span class="hud-item">O: ₹{hoveredCandle.open.toFixed(2)}</span>
            <span class="hud-item">H: ₹{hoveredCandle.high.toFixed(2)}</span>
            <span class="hud-item">L: ₹{hoveredCandle.low.toFixed(2)}</span>
            <span class="hud-item">C: ₹{hoveredCandle.close.toFixed(2)}</span>
            <span class="hud-item hud-vwap">VWAP: ₹{hoveredCandle.vwap?.toFixed(2) || '-'}</span>
          {:else if chartData?.candles?.length > 0}
            {@const last = chartData.candles[chartData.candles.length - 1]}
            <span class="hud-item hud-ltp">LTP: ₹{last.close.toFixed(2)}</span>
            <span class="hud-item hud-vwap">VWAP: ₹{last.vwap?.toFixed(2) || '-'}</span>
          {/if}

          {#if chartData?.active_position}
            <span class="hud-item hud-sl">SL: ₹{chartData.active_position.trailing_sl_price?.toFixed(2) || '-'}</span>
            <span class="hud-item hud-tgt">TGT: ₹{chartData.active_position.target_price?.toFixed(2) || '-'}</span>
          {/if}
        </div>

        <button type="button" class="chart-close-btn" on:click={onClose}>✕</button>
      </div>

      <!-- Canvas Body -->
      <div class="chart-canvas-container">
        {#if isLoading}
          <div class="chart-loading-overlay">
            <div class="spinner"></div>
            <span>Loading live signal chart...</span>
          </div>
        {:else if errorMessage}
          <div class="chart-error-overlay">
            <span>⚠️ {errorMessage}</span>
            <button type="button" class="chart-retry-btn" on:click={fetchChartData}>Retry</button>
          </div>
        {/if}

        <canvas
          bind:this={canvas}
          width="860"
          height="420"
          on:mousemove={handleMouseMove}
          on:mouseleave={handleMouseLeave}
        ></canvas>
      </div>

      <!-- Footer Legend Strip -->
      <div class="chart-footer">
        <div class="legend-item"><span class="legend-dot" style="background:#16a34a;"></span> Bullish Bar</div>
        <div class="legend-item"><span class="legend-dot" style="background:#dc2626;"></span> Bearish Bar</div>
        <div class="legend-item"><span class="legend-line" style="background:#3b82f6;"></span> Intraday VWAP</div>
        <div class="legend-item"><span class="legend-line-dashed" style="border-color:#ef4444;"></span> Trailing SL</div>
        <div class="legend-item"><span class="legend-line-dashed" style="border-color:#10b981;"></span> Profit Target</div>
        <div class="legend-item"><span class="legend-line-dotted" style="border-color:#f59e0b;"></span> Down-Avg Dips</div>
        {#if chartData?.matrix_config}
          <div class="matrix-stats">
            H: {chartData.matrix_config.h_qty} | M: {chartData.matrix_config.m_qty} | L: {chartData.matrix_config.l_qty} | Tgt: {chartData.matrix_config.target_pct}% | SL: {chartData.matrix_config.stl_pct}%
          </div>
        {/if}
      </div>
    </div>
  </div>
{/if}

<style>
  .signal-chart-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.75);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
  }
  .signal-chart-modal {
    background: #0b1329;
    border: 1px solid #1e293b;
    border-radius: 12px;
    width: 900px;
    max-width: 95vw;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.8), 0 0 20px rgba(59, 130, 246, 0.2);
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
  .chart-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 18px;
    background: #0f172a;
    border-bottom: 1px solid #1e293b;
  }
  .chart-title-area {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .chart-symbol {
    font-size: 16px;
    font-weight: 800;
    color: #f8fafc;
    letter-spacing: 0.5px;
  }
  .chart-scrip-code {
    font-size: 12px;
    font-weight: 600;
    color: #94a3b8;
    font-family: monospace;
  }
  .chart-badge-ucc {
    font-size: 11px;
    font-weight: 600;
    color: #94a3b8;
    background: #1e293b;
    padding: 2px 8px;
    border-radius: 4px;
    font-family: monospace;
  }
  .chart-hud-metrics {
    display: flex;
    align-items: center;
    gap: 12px;
    font-family: monospace;
    font-size: 12px;
  }
  .hud-item {
    color: #94a3b8;
  }
  .hud-ltp {
    color: #f8fafc;
    font-weight: 700;
  }
  .hud-vwap {
    color: #60a5fa;
    font-weight: 600;
  }
  .hud-sl {
    color: #f87171;
  }
  .hud-tgt {
    color: #34d399;
  }
  .chart-close-btn {
    background: transparent;
    border: none;
    color: #94a3b8;
    font-size: 18px;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 4px;
  }
  .chart-close-btn:hover {
    color: #f8fafc;
    background: #1e293b;
  }
  .chart-canvas-container {
    position: relative;
    width: 100%;
    height: 420px;
    background: #0f172a;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  canvas {
    display: block;
    cursor: crosshair;
  }
  .chart-loading-overlay, .chart-error-overlay {
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(15, 23, 42, 0.85);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    color: #94a3b8;
    font-size: 13px;
    z-index: 5;
  }
  .spinner {
    width: 24px;
    height: 24px;
    border: 3px solid rgba(59, 130, 246, 0.2);
    border-top-color: #3b82f6;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  .chart-retry-btn {
    background: #2563eb;
    border: none;
    color: white;
    padding: 4px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 600;
  }
  .chart-footer {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 14px;
    padding: 10px 18px;
    background: #0b1329;
    border-top: 1px solid #1e293b;
    font-size: 11px;
    color: #64748b;
  }
  .legend-item {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .legend-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }
  .legend-line {
    width: 14px;
    height: 2px;
  }
  .legend-line-dashed {
    width: 14px;
    height: 0px;
    border-top: 2px dashed;
  }
  .legend-line-dotted {
    width: 14px;
    height: 0px;
    border-top: 2px dotted;
  }
  .matrix-stats {
    margin-left: auto;
    font-family: monospace;
    color: #94a3b8;
  }
</style>
